import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ChevronRight, Download, Loader2, Monitor, Plus, Building2, X } from "lucide-react";
import { useInfiniteUserTerminals, useRemoveTerminalFromOrganization } from "@/domains/terminals/services/user-terminals";
import { UserTerminal } from "@/shared/types/userTerminalTypes";
import UserTerminalCard from '@/domains/terminals/components/UserTerminalCard';
import SearchBar from "@/shared/components/SearchBar";
import ContainerLayout from '@/shared/components/ContainerLayout';
import EmptyView from '@/shared/components/EmptyView';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import AddTerminalsToAccountDialog from '@/domains/terminals/dialogs/AddTerminalsToAccountDialog';
import { Button } from '@/shared/ui/button';
import AddTerminalsToStarlinkDialog from '@/domains/terminals/dialogs/AddTerminalsToStarlinkDialog';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError } from '@/shared/utils/toast-utils';
import { showSuccess } from '@/shared/utils/toast-utils';
import { useRemoveTerminalFromStarlink } from '@/domains/terminals/services/user-terminals';
import { useUser } from '@/contexts/UserContext';
import { TerminalTypeDialog } from '@/domains/terminals/dialogs/TerminalTypeDialog';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Organization } from '@/shared/types/userTypes';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { TerminalType } from '@/shared/types/userTerminalTypes';
import TransferTerminalsToAccountDialog from '@/domains/terminals/dialogs/TransferTerminalsToAccountDialog';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { Separator } from '@/shared/ui/separator';
import { TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { OrgType } from '@/shared/types/userRoleTypes';
import { Feature, Permission } from '@/shared/types/permissionsTypes';

const unassignedAccountKey = 'Unassigned Terminals';
const UserTerminalsPage = () => {
  const { selectedRegionCode } = useUser();
  const { switchOrganizationAndNavigateToAccountDetails, switchOrganizationAndNavigateToServiceLineDetails } = useRouteNavigation();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.PLATFORM, Feature.TERMINALS, Permission.MANAGE);

  const queryClient = useQueryClient();
  const {
    data: terminalsInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch: refetchTerminals,
  } = useInfiniteUserTerminals(selectedRegionCode);

  // State for generic alert dialog
  const [isAddTerminalsToOrganizationDialogOpen, setIsAddTerminalsToOrganizationDialogOpen] = useState(false);
  const [isAddTerminalsToStarlinkDialogOpen, setIsAddTerminalsToStarlinkDialogOpen] = useState(false);
  const [isRemoveStarlinkDialogOpen, setIsRemoveStarlinkDialogOpen] = useState(false);
  const [isTerminalTypeDialogOpen, setIsTerminalTypeDialogOpen] = useState(false);
  const [isRemoveOrganizationDialogOpen, setIsRemoveOrganizationDialogOpen] = useState(false);
  const [isTransferTerminalsToOrganizationDialogOpen, setIsTransferTerminalsToOrganizationDialogOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [terminalTypeFilter, setTerminalTypeFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [selectedTerminal, setSelectedTerminal] = useState<UserTerminal | null>(null);
  const [transferTerminals, setTransferTerminals] = useState<UserTerminal[]>([]);
  const getTerminalAccountKey = (terminal: UserTerminal) => terminal.organization?.name?.trim() || unassignedAccountKey;

  const terminals = useMemo(() => {
    const pages = terminalsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: UserTerminal[] = [];
    for (const page of pages) {
      for (const t of page.results ?? []) {
        if (!seen.has(t.userTerminalId)) {
          seen.add(t.userTerminalId);
          merged.push(t);
        }
      }
    }
    // Sort terminals by service line not null first
    merged.sort((a, b) => (a.serviceLine ? -1 : 1) - (b.serviceLine ? -1 : 1));
    return merged;
  }, [terminalsInfiniteData?.pages]);

  const totalCount = terminalsInfiniteData?.pages[0]?.totalCount ?? 0;

  // Filter audit logs based on current filters
  const filteredTerminals = useMemo(() => {
    if (!terminals || (terminals?.length ?? 0) === 0) return [];

    return terminals?.filter(terminal => {
      // Terminal type filter
      if (terminalTypeFilter !== 'all' && terminal.type !== terminalTypeFilter) return false;

      // Account filter
      if (accountFilter !== 'all') {
        const accountKey = getTerminalAccountKey(terminal);
        if (accountKey !== accountFilter) {
          return false;
        }
      }

      const searchLower = searchTerm.toLowerCase();
      // Search filter
      if (searchLower &&
        !terminal.userTerminalId?.toLowerCase().includes(searchLower) &&
        !terminal.kitSerialNumber?.toLowerCase().includes(searchLower) &&
        !terminal.dishSerialNumber?.toLowerCase().includes(searchLower) &&
        !terminal.serviceLine?.serviceLineName?.toLowerCase().includes(searchLower) &&
        !terminal.serviceLine?.serviceLineNumber?.toLowerCase().includes(searchLower) &&
        !terminal.organization?.name?.toLowerCase().includes(searchLower) &&
        !terminal.organization?.organizationId?.toLowerCase().includes(searchLower)) {
        return false;
      }

      return true;
    });
  }, [terminals, terminalTypeFilter, searchTerm, accountFilter]);

  // Group terminals by account
  const groupedTerminalsByAccount = useMemo(() => {
    if (!terminals) return {};

    const groups: Record<string, UserTerminal[]> = {};

    terminals.forEach(terminal => {
      const accountKey = getTerminalAccountKey(terminal);
      if (!groups[accountKey]) {
        groups[accountKey] = [];
      }
      groups[accountKey].push(terminal);
    });

    return groups;
  }, [terminals]);

  // Group terminals by account
  const groupedTerminals = useMemo(() => {
    if (!filteredTerminals) return {};

    const groups: Record<string, UserTerminal[]> = {};

    filteredTerminals.forEach(terminal => {
      const accountKey = getTerminalAccountKey(terminal);
      if (!groups[accountKey]) {
        groups[accountKey] = [];
      }
      groups[accountKey].push(terminal);
    });

    return groups;
  }, [filteredTerminals]);

  const refetchTerminalsData = useCallback(() => {
    if (selectedRegionCode) {
      void queryClient.resetQueries({ queryKey: ['region-user-terminals-infinite', selectedRegionCode] });
    }
  }, [queryClient, selectedRegionCode]);

  // Manage Account
  const onManageOrganization = async (organization: Organization) => {
    switchOrganizationAndNavigateToAccountDetails({ organization, accountDetailsTab: AccountDetailsTab.TERMINALS });
  };

  const handleManageTerminal = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal);
    setIsTerminalTypeDialogOpen(true);
  };

  // Account functions
  const handleAddTerminalToOrganization = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal);
    setIsAddTerminalsToOrganizationDialogOpen(true);
  };

  const handleRemoveTerminalFromOrganization = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal);
    setIsRemoveOrganizationDialogOpen(true);
  };

  const removeTerminalFromOrganization = useRemoveTerminalFromOrganization();

  const confirmRemoveTerminalFromOrganization = async () => {
    if (!selectedTerminal || !selectedTerminal.organization?.organizationId) return;
    try {
      await removeTerminalFromOrganization.mutateAsync({
        organizationId: selectedTerminal.organization.organizationId,
        terminalId: selectedTerminal.userTerminalId
      });
      showSuccess(SUCCESS.TERMINAL.REMOVED_FROM_ACCOUNT);
      refetchTerminalsData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.REMOVE_FROM_ACCOUNT);
    } finally {
      setIsRemoveOrganizationDialogOpen(false);
      setSelectedTerminal(null);
    }
  };

  // Starlink functions
  const handleAddTerminalToStarlink = () => {
    setIsAddTerminalsToStarlinkDialogOpen(true);
  };

  const handleRemoveTerminalFromStarlink = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal);
    setIsRemoveStarlinkDialogOpen(true);
  };

  const removeTerminalFromStarlink = useRemoveTerminalFromStarlink();

  const confirmRemoveTerminalFromStarlink = async () => {
    if (!selectedTerminal) return;
    try {
      await removeTerminalFromStarlink.mutateAsync(selectedTerminal.userTerminalId);
      showSuccess(SUCCESS.TERMINAL.REMOVED_FROM_STARLINK);
      refetchTerminalsData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.REMOVE_FROM_STARLINK);
    } finally {
      setIsRemoveStarlinkDialogOpen(false);
      setSelectedTerminal(null);
    }
  };

  const handleManageServiceLineForOrganization = async (organization: Organization, serviceLine: ServiceLine) => {
    switchOrganizationAndNavigateToServiceLineDetails(organization, serviceLine.serviceLineNumber);
  };

  const handleTransferTerminalsToOrganization = (terminals: UserTerminal[]) => {
    if (terminals.length === 0) return;
    setIsTransferTerminalsToOrganizationDialogOpen(true);
    setTransferTerminals(terminals);
  };

  // Handle export action
  const handleExportAction = () => {
    // If there is no data to export, show an alert
    if (!terminals || terminals.length === 0) {
      alert('No data to export');
      return;
    }

    // If the number of service lines is less than the total count, ask for confirmation
    if (terminals.length < totalCount) {
      if (confirm(`Only ${terminals.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
        handleExport();
      }
      return;
    }

    // Export the data
    handleExport();
  }

  const handleExport = () => {
    const csvData = terminals.map(terminal => {
      const accountName = terminal.organization?.name || '-';
      const organizationId = terminal.organization?.organizationId || '-';
      const terminalId = terminal.userTerminalId;
      const kitSerialNumber = terminal.kitSerialNumber;
      const dishSerialNumber = terminal.dishSerialNumber;
      const terminalType = terminal.type || 'Not Set';
      const terminalStatus = terminal.serviceLine ? "Deployed" : "Not Deployed";
      const serviceLine = terminal.serviceLine;
      const serviceLineName = serviceLine?.serviceLineName || '-';
      const serviceLineNumber = serviceLine?.serviceLineNumber || '-';
      const serviceLineStatus = serviceLine?.status || '-';
      const serviceLineType = serviceLine?.type || '-';
      const startDate = serviceLine?.startDate ? formatDateToLocaleString(serviceLine.startDate) : '-';

      return {
        'Datapool': selectedRegionCode,
        'Account Name': accountName,
        'Account ID': organizationId,
        'Terminal ID': terminalId,
        'Kit Serial Number': kitSerialNumber,
        'Dish Serial Number': dishSerialNumber,
        'Terminal Type': terminalType || '-',
        'Terminal Status': terminalStatus || '-',
        'Service Line Name': serviceLineName || '-',
        'Service Line Number': serviceLineNumber || '-',
        'Service Line Status': serviceLineStatus || '-',
        'Service Line Type': serviceLineType || '-',
        'Start Date': startDate || '-',
      };
    });

    exportToCsv(csvData, `terminals_${selectedRegionCode || 'region'}`);
  };

  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Terminals</h1>
            <p className="text-lg">Manage terminals for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            {handleAddTerminalToStarlink && canManage && (
              <Button
                variant="default"
                className="gap-2"
                onClick={handleAddTerminalToStarlink}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4" />
                Add Terminal(s) to Starlink
              </Button>
            )}
            {terminals && terminals.length > 0 && (
              <Button onClick={handleExportAction} className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </div>
        </div>
        {/* Search Bar */}
        {terminals && terminals.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 justify-between">
              <div className="relative w-full max-w-1/2">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search terminals by ID, kit serial, dish serial, account name, or service line number/name..."
                />
              </div>
              {/* Filters */}
              {/* Account Filter */}
              <div className="flex items-center gap-2 min-w-64">
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {Object.keys(groupedTerminalsByAccount).map((accountKey) => (
                      <SelectItem key={accountKey} value={accountKey}>
                        {accountKey}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Terminal Type Filter */}
              <div className="flex items-center gap-2 min-w-64">
                <Select value={terminalTypeFilter} onValueChange={setTerminalTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Terminal Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.values(TerminalType).map((type) => (
                      <SelectItem key={type} value={type}>
                        {TERMINAL_TYPE_INFO[type].value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" onClick={() => {
                setTerminalTypeFilter('all');
                setAccountFilter('all');
                setSearchTerm('');
              }}>
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading Terminals...</p>
          </div>
        ) : (
          error ? (
            <EmptyView
              icon={Monitor}
              title="Error Loading Terminals"
              description="Error loading terminals at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchTerminals}
            />
          ) : (
            <>
              {/* Scrollable Content Section */}
              {filteredTerminals && filteredTerminals.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {Object.entries(groupedTerminals).map(([accountKey, terminals]) => {
                    const organization = terminals[0]?.organization;
                    const unassignedTerminals = terminals.filter(terminal => terminal.serviceLine == null);

                    return (
                      <Card key={accountKey} className="flex flex-col gap-4">
                        {/* Account Header */}
                        <CardHeader
                          className={`p-4 pb-0 ${organization ? 'cursor-pointer' : ''}`}
                          onClick={() => organization && onManageOrganization(organization)}>
                          <div className="flex justify-between">

                            <div className="flex flex-row items-center gap-2">
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center border">
                                <Building2 className="h-6 w-6" />
                              </div>
                              <div>
                                <CardTitle className="text-xl font-semibold">{accountKey}</CardTitle>
                              </div>
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              {unassignedTerminals.length > 0 && canManage && (
                                <Button
                                  variant="outline"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleTransferTerminalsToOrganization(unassignedTerminals);
                                  }}
                                  disabled={isTransferTerminalsToOrganizationDialogOpen}
                                  className="gap-2">
                                  <ArrowRightLeft className="h-4 w-4" />
                                  Transfer Terminal{unassignedTerminals.length > 1 ? 's' : ''} ({unassignedTerminals.length})
                                </Button>
                              )}
                              <Badge variant="default">
                                {terminals.length} terminal{terminals.length > 1 ? 's' : ''}
                              </Badge>
                              {organization && <ChevronRight className="h-8 w-8" />}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="gap-4 space-y-4 pb-4 px-4">
                          {/* Terminals Grid */}
                          {terminals.map((terminal) => (
                            <UserTerminalCard
                              key={terminal.userTerminalId}
                              terminal={terminal}
                              onManageTerminal={canManage ? () => handleManageTerminal(terminal) : undefined}
                              onAddTerminalToOrganization={canManage ? () => handleAddTerminalToOrganization(terminal) : undefined}
                              onRemoveTerminalFromOrganization={canManage ? () => handleRemoveTerminalFromOrganization(terminal) : undefined}
                              onRemovedTerminalFromStarlink={canManage ? () => handleRemoveTerminalFromStarlink(terminal) : undefined}
                              onTransferTerminalToOrganization={canManage ? () => handleTransferTerminalsToOrganization([terminal]) : undefined}
                              onManageServiceLine={() => handleManageServiceLineForOrganization(organization, terminal.serviceLine)}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}

                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{terminals.length}</span>
                        {' '}of <span className="font-bold">{totalCount}</span> terminals
                      </span>
                      {filteredTerminals.length !== terminals.length && (
                        <span className="text-sm">
                          ({filteredTerminals.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Monitor}
                  title={searchTerm ? "No Matching Terminals Found" : "No Terminals Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no terminals available."}
                  actionLabel={hasNextPage ? "Load More" : undefined}
                  onAction={hasNextPage ? () => void fetchNextPage() : undefined}
                />
              )}
            </>
          ))}
      </div>

      {/* Terminal Dialogs */}
      {
        selectedTerminal && (
          <>
            <AddTerminalsToAccountDialog
              open={isAddTerminalsToOrganizationDialogOpen}
              onOpenChange={setIsAddTerminalsToOrganizationDialogOpen}
              onSuccess={refetchTerminals}
              terminal={selectedTerminal}
              selectedRegionCode={selectedRegionCode}
            />

            <TerminalTypeDialog
              open={isTerminalTypeDialogOpen}
              onOpenChange={setIsTerminalTypeDialogOpen}
              onSuccess={refetchTerminals}
              terminal={selectedTerminal}
            />
          </>
        )
      }

      {/* Transfer Terminal(s) to Organization Dialog */}
      <TransferTerminalsToAccountDialog
        open={isTransferTerminalsToOrganizationDialogOpen}
        onOpenChange={setIsTransferTerminalsToOrganizationDialogOpen}
        onSuccess={refetchTerminals}
        terminals={transferTerminals}
        selectedRegionCode={selectedRegionCode}
      />

      {/* Remove Terminal from Organization Dialog */}
      <GenericAlertDialog
        open={isRemoveOrganizationDialogOpen}
        onOpenChange={setIsRemoveOrganizationDialogOpen}
        title="Remove Terminal from Organization"
        description="Are you sure you want to remove this terminal from the organization?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveTerminalFromOrganization}
        loading={removeTerminalFromOrganization.isPending}
      />

      {/* Add Terminal to Starlink Dialog */}
      <AddTerminalsToStarlinkDialog
        open={isAddTerminalsToStarlinkDialogOpen}
        onOpenChange={setIsAddTerminalsToStarlinkDialogOpen}
        onSuccess={refetchTerminals}
        selectedRegionCode={selectedRegionCode}
      />

      {/* Remove Terminal from Starlink Dialog */}
      <GenericAlertDialog
        open={isRemoveStarlinkDialogOpen}
        onOpenChange={setIsRemoveStarlinkDialogOpen}
        title="Remove Terminal from Starlink"
        description="Are you sure you want to remove this terminal from Starlink?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveTerminalFromStarlink}
        loading={removeTerminalFromStarlink.isPending}
      />
    </ContainerLayout >
  );
};

export default UserTerminalsPage;